import { useState, useCallback } from 'preact/hooks'
import { assetApi, type SendTestEmailRequestDto } from '@/api/asset'
import { useToast } from '@/hooks/useToast'
import { useUserProfile } from '@/hooks/useUserProfile'

interface SendTestArgs {
  clientCompanyId: string
  workspaceId: string
  campaignId: string
  assetId: string
  /** Optional body — omit FromSendingDomainId for the platform "copy-editor" test;
   *  pass HtmlOverride to test the exact HTML on screen (live editor export or the
   *  version under review) without a save. */
  body?: SendTestEmailRequestDto
}

/**
 * Shared "Send Test" action for an Email Copy asset (ORB-362, surfaced by ORB-441).
 *
 * Wraps the existing `sendTestEmail` endpoint with the busy flag + toast handling first
 * written for the activation panel, so the Email Builder and the asset review screen can
 * reuse the identical behaviour. The recipient is always the authenticated caller — the
 * server derives it and ignores anything in the body — so this only ever mails the user
 * their own copy; `recipientEmail` is surfaced purely to name the address in the toast.
 */
export function useSendTestEmail() {
  const [sendingTest, setSendingTest] = useState(false)
  const { showSuccess, showError } = useToast()
  const { userProfile } = useUserProfile()
  const recipientEmail = userProfile?.PrimaryEmail

  const sendTest = useCallback(async ({
    clientCompanyId,
    workspaceId,
    campaignId,
    assetId,
    body,
  }: SendTestArgs): Promise<boolean> => {
    setSendingTest(true)
    try {
      const res = await assetApi.sendTestEmail(clientCompanyId, workspaceId, campaignId, assetId, body ?? {})
      if (res.data?.Sent) {
        showSuccess('Test email sent', `Sent to ${recipientEmail ?? 'your address'}.`)
        return true
      }
      showError('Test not sent', res.data?.FailureReason || res.error || 'Could not send the test email.')
      return false
    } catch {
      showError('Test not sent', 'Could not send the test email.')
      return false
    } finally {
      setSendingTest(false)
    }
  }, [showSuccess, showError, recipientEmail])

  return { sendTest, sendingTest, recipientEmail }
}
